import React from 'react'
import { useHistory } from 'react-router-dom'
import { useExtensionInstalled } from '@mote/helpers/hooks/useExtensionInstalled'
import { Block } from '@mote/ui'
import {
  moteConversationDataGetter,
  useMoteConversationMutation,
  useTrackMutation
} from '@mote/api'
import { Emoji, useRecordReplySidebar } from '@mote/presentation'
import { useQueryParams } from '@mote/helpers'
import { Logo } from '@mote/assets/icons'
import {
  addExtensionFoundListener,
  checkWildCardOriginsPermissions,
  requestWildCardOriginsPermissions,
  broadcastExternalWebsiteRedirection
} from '../../shared/Auth'
import { MoteLandingLayout } from './MoteLandingLayout'
import {
  Error,
  TransitionContainer,
  TransitionContentContainer,
  TransitionText
} from './layout'
import { Mote } from './Mote'
import { useLocalStoragePermission } from '@mote/helpers/browser'

export const MoteLandingScreen = props => {
  const { state: sidebarState, loadMote } = useRecordReplySidebar()
  const { queryParams } = useQueryParams()
  const { mutate: track } = useTrackMutation()
  const history = useHistory()
  const canAccessLocalStorage = useLocalStoragePermission()

  const {
    data,
    error,
    mutate: fetchConversation
  } = useMoteConversationMutation(
    props.match.params.moteId,
    sidebarState.refetchReplies ? 2000 : false
  )

  const mote = React.useMemo(() => {
    if (!data) {
      const { motePreview } = window

      if (motePreview) {
        return {
          isPrefetch: true,
          parentMote: {
            ...motePreview.info,
            ...motePreview.transcript,
            ...motePreview.sticky,
            currentMoteId: motePreview.info.moteId,
            createdByGreeting: motePreview.info.createdByName.split(' ')[0],
            duration: motePreview.audio.duration,
            niceTitle: motePreview.info.title,
            playbackLocation: motePreview.audio.playbackLocation,
            waveform: motePreview.audio.waveform,
            waveformFrameMilliseconds: motePreview.audio.frameMilliseconds,
            myReactions: [],
            availableReactions: [],
            availableUnreactions: []
          }
        }
      }

      return null
    }

    return moteConversationDataGetter(data)
  }, [data])

  React.useEffect(() => {
    if (window.location !== window.parent.location) {
      history.push(`/embed/${props.match.params.moteId}`)
    } else {
      fetchConversation()
    }
  }, [])

  React.useEffect(() => {
    if (!mote) return

    if (!mote.isPrefetch && !sidebarState.mote) {
      const isChildReply =
        mote.parentMote.currentMoteId !== mote.parentMote.moteId

      const isOpen = isChildReply

      const options = {
        isOpen,
        highlightId: isChildReply ? mote.currentMoteId : null
      }
      loadMote(mote, options)
    }
  }, [mote, sidebarState, loadMote])

  const [hasRedirectionPermissions, setHasRedirectionPermissions] =
    React.useState(null)

  const extensionInstalled = useExtensionInstalled()

  const shouldRedirect = React.useCallback(() => {
    if (queryParams.skipRedirect || queryParams.embed) return false
    if (
      !canAccessLocalStorage ||
      (canAccessLocalStorage &&
        localStorage.getItem('mote-prevent-redirection'))
    )
      return false

    return (
      extensionInstalled &&
      hasRedirectionPermissions &&
      mote?.parentMote?.canRedirect
    )
  }, [
    extensionInstalled,
    hasRedirectionPermissions,
    mote,
    queryParams,
    canAccessLocalStorage
  ])

  React.useEffect(() => {
    addExtensionFoundListener(result => {
      setHasRedirectionPermissions(result.success)
    })
    checkWildCardOriginsPermissions()
  }, [])

  React.useEffect(() => {
    if (error || !shouldRedirect()) return

    track({
      variables: {
        event: 'Embedded Mote link redirected',
        content_asset_id: mote.parentMote.moteId
      }
    })
    broadcastExternalWebsiteRedirection({
      redirect: {
        moteId: mote.parentMote.moteId,
        redirectTo: mote.parentMote.containingURL
      }
    })
    window.location.href = mote.parentMote.containingURL
  }, [mote, shouldRedirect, error])

  function handleRequestRedirectionPermissions() {
    addExtensionFoundListener(result => {
      setHasRedirectionPermissions(result.success)
    })

    requestWildCardOriginsPermissions()
  }

  if (error) {
    return (
      <MoteLandingLayout
        hero={
          <Block justify='center' align='center' margin='20px 0 0 0'>
            <Error className='p-2 m-2'>
              <p>
                Sorry... Could not load this mote! <Emoji>😢</Emoji>
              </p>
              <p>
                The mote may have been deleted, or the link may be incorrect.
              </p>
            </Error>
          </Block>
        }
      />
    )
  }

  if (mote && !shouldRedirect()) {
    return (
      <Mote
        mote={mote}
        hasRedirectionPermissions={hasRedirectionPermissions}
        extensionInstalled={extensionInstalled}
        onRequestRedirectionPermissions={handleRequestRedirectionPermissions}
        stripLayout={Boolean(queryParams.embed)}
      />
    )
  }

  return (
    <TransitionContainer>
      <TransitionContentContainer>
        {/* src need to be replace with `/images/logos/mote-lockup-header.png` */}
        <Logo />
        <TransitionText>Just a second...</TransitionText>
        <TransitionText className='mt-0'>Checking the link.</TransitionText>
      </TransitionContentContainer>
    </TransitionContainer>
  )
}
